import React, { useEffect } from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccessTime,
  BarChart,
  Business,
  CheckCircle,
  EventAvailable,
  ExitToApp,
  Group,
  Home,
  PeopleAlt,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/ponto-now-logo.png';
import { canSeeManagerNavigation, readSessionUser } from '../utils/sessionUser';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <Home />, path: '/dashboard', managerOnly: true },
  { text: 'Registrar ponto', icon: <AccessTime />, path: '/ponto', managerOnly: false },
  { text: 'Aprovar Pontos', icon: <CheckCircle />, path: '/point-approval', managerOnly: false },
  { text: 'Eventos', icon: <EventAvailable />, path: '/events', managerOnly: true },
  { text: 'Participantes', icon: <PeopleAlt />, path: '/participantes', managerOnly: true },
  { text: 'Presença do evento', icon: <EventAvailable />, path: '/presenca', managerOnly: true },
  { text: 'Gerenciamento de Usuários', icon: <Group />, path: '/users', managerOnly: true },
  { text: 'Organizações', icon: <Business />, path: '/organizations', managerOnly: true },
  { text: 'Relatórios', icon: <BarChart />, path: '/reports', managerOnly: true },
];

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const user = readSessionUser();
  const showManagerItems = canSeeManagerNavigation(user);
  const visibleItems = menuItems.filter((item) => !item.managerOnly || showManagerItems);

  useEffect(() => {
    if (isMobile) onClose();
  }, [isMobile, location.pathname, onClose]);

  const goTo = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: theme.pontonow.canvas,
          color: theme.pontonow.textPrimary,
          borderRight: `1px solid ${theme.pontonow.border}`,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Toolbar sx={{ minHeight: 88, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box component="img" src={logo} alt="Logo Ponto Now" sx={{ width: 46, height: 46 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>Ponto Now</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Gestão inteligente de ponto
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <List id="navegacao-principal" sx={{ px: 1.5, py: 1 }} aria-label="Navegação principal">
        {visibleItems.map((item) => {
          const selected = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <ListItemButton
              key={item.path}
              selected={selected}
              onClick={() => goTo(item.path)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                minHeight: 44,
                color: theme.pontonow.textSecondary,
                backgroundColor: 'transparent',
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: 'inherit',
                },
                '&&:hover': {
                  backgroundColor: theme.pontonow.navHover,
                  color: theme.pontonow.textPrimary,
                },
                '&&.Mui-selected': {
                  backgroundColor: theme.pontonow.navActive,
                  color: theme.pontonow.textPrimary,
                  fontWeight: 600,
                },
                '&&.Mui-selected:hover': {
                  backgroundColor: theme.pontonow.navActive,
                  color: theme.pontonow.textPrimary,
                },
                '&&.Mui-focusVisible': {
                  color: theme.pontonow.textPrimary,
                  backgroundColor: selected ? theme.pontonow.navActive : theme.pontonow.navHover,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ sx: { color: 'inherit', fontWeight: selected ? 600 : 500 } }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List sx={{ px: 1.5, py: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            minHeight: 44,
            color: 'error.main',
            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary="Sair" />
        </ListItemButton>
      </List>
    </Drawer>
  );
};

export default Sidebar;
